// Config-driven assessment template model (prompt Part 6).
//
// A tenant configures assessment templates: sections, template custom fields,
// assessment questions, candidate affected objects and preliminary activities.
// The renderer works entirely from this configuration, so another tenant can
// change titles, fields, types, options, required state, visibility rules,
// defaults, suggested answers, responsible functions and candidates without any
// code change. Nothing here uses `any`.
//
// Three distinct field layers are represented (prompt Part 3):
//   1. Platform fields  - stable, code-owned, shared by every Change.
//   2. Template custom fields - organization-configured structured facts.
//   3. Assessment questions   - controlled review/decision points.

import type { TSparseItem } from '@modules/items';

import type { TTaskPriority, TTaskType } from './plan';
import type { ImplementationProposal } from '../../change/types';

// -- Layer 1: platform field keys a template may prefill (never redefine).
export const SYSTEM_CHANGE_FIELD_KEY = {
  TITLE: 'title',
  DESCRIPTION: 'description',
  REASON_OR_TRIGGER: 'reasonOrTrigger',
  DESIRED_OUTCOME: 'desiredOutcome',
  IMPLEMENTATION_PROPOSAL: 'implementationProposal',
  CHANGE_MANAGER_ID: 'changeManagerId',
  TARGET_DATE: 'targetDate',
  CHANGE_CATEGORY: 'changeCategory',
  SCOPE: 'scope',
  CRITICALITY: 'criticality',
  URGENCY: 'urgency',
  AFFECTED_PRODUCT_IDS: 'affectedProductIds',
  AFFECTED_QMS_AREA_IDS: 'affectedQmsAreaIds',
  SOURCE_RECORD_ID: 'sourceRecordId',
  KNOWN_SAFETY_CONCERN: 'knownSafetyConcern',
} as const;
export type TSystemChangeFieldKey = (typeof SYSTEM_CHANGE_FIELD_KEY)[keyof typeof SYSTEM_CHANGE_FIELD_KEY];

// -- Control types shared by template custom fields and assessment questions.
export const TEMPLATE_FIELD_TYPE = {
  YES_NO_UNKNOWN: 'yesNoUnknown',
  SINGLE_CHOICE: 'singleChoice',
  MULTI_SELECT: 'multiSelect',
  SHORT_TEXT: 'shortText',
  LONG_TEXT: 'longText',
  DATE: 'date',
  PERSON: 'person',
  ENTITY_REFERENCE: 'entityReference',
  EVIDENCE_REFERENCE: 'evidenceReference',
} as const;
export type TTemplateFieldType = (typeof TEMPLATE_FIELD_TYPE)[keyof typeof TEMPLATE_FIELD_TYPE];

// Assessment questions reuse the same renderer/control set as custom fields, but
// stay a semantically distinct concept (they carry completion rules, rationale,
// evidence, candidate-impact and follow-up behaviour).
export type TAssessmentControlType = TTemplateFieldType;

// Where a prefilled value came from. Drives the subtle origin label shown in the UI.
export const PREFILL_KIND = {
  REQUEST_FACT: 'requestFact', // a fact copied from the submitted request
  TEMPLATE_DEFAULT: 'templateDefault', // an organization-configured default
  TEMPLATE_SUGGESTION: 'templateSuggestion', // a suggestion the assessor must confirm
  NONE: 'none',
} as const;
export type TPrefillKind = (typeof PREFILL_KIND)[keyof typeof PREFILL_KIND];

// Lifecycle of a single value's confirmation, so a suggestion is never treated as
// a confirmed answer until the assessor accepts or changes it.
export const CONFIRMATION_STATE = {
  UNCONFIRMED: 'Unconfirmed',
  CONFIRMED_FROM_REQUEST: 'Confirmed from request',
  CONFIRMED_BY_ASSESSOR: 'Confirmed by assessor',
  CHANGED_BY_ASSESSOR: 'Changed by assessor',
} as const;
export type TConfirmationState = (typeof CONFIRMATION_STATE)[keyof typeof CONFIRMATION_STATE];

export type TEntityReferenceValue = {
  id: string;
  label: string;
  entityType: string;
};

// The full value space a field or answer can hold. Deliberately explicit (no `any`).
export type TFieldValue = string | boolean | number | null | string[] | TEntityReferenceValue | TEntityReferenceValue[];

// -- Conditional visibility / required rules (prompt Part 3).
export const CONDITION_OPERATOR = {
  EQUALS: 'equals',
  NOT_EQUALS: 'notEquals',
  CONTAINS: 'contains',
  IN: 'in',
  IS_ANSWERED: 'isAnswered',
} as const;
export type TConditionOperator = (typeof CONDITION_OPERATOR)[keyof typeof CONDITION_OPERATOR];

export type TFieldCondition = {
  sourceKey: string; // a custom field id, question id or platform field key
  operator: TConditionOperator;
  value?: TFieldValue;
};

export type TConditionGroup = {
  combinator: 'and' | 'or';
  conditions: Array<TFieldCondition | TConditionGroup>;
};

export type TConditionExpression = TFieldCondition | TConditionGroup;

// -- Layer 2: a template custom field (organization-configured structured fact).
export type TTemplateCustomFieldConfig = {
  id: string;
  label: string;
  helpText?: string;
  fieldType: TTemplateFieldType;
  sectionId: string;
  required: boolean;
  options?: string[];
  defaultValue?: TFieldValue;
  prefillKind: TPrefillKind;
  prefilledValue?: TFieldValue;
  visibleWhen?: TConditionExpression;
  requiredWhen?: TConditionExpression;
};

// -- Layer 3: an assessment question (controlled review/decision point).
export type TAssessmentQuestionConfig = {
  id: string;
  label: string;
  helpText?: string;
  controlType: TAssessmentControlType;
  required: boolean;
  options?: string[];
  defaultValue?: TFieldValue;
  prefillKind: TPrefillKind;
  prefilledValue?: TFieldValue;
  /** A prefilled free-text comment shown alongside the control (e.g. multi-select plus comment). */
  prefilledComment?: string;
  /** Whether the control offers an additional free-text comment. */
  allowsComment?: boolean;
  visibleWhen?: TConditionExpression;
  requiredWhen?: TConditionExpression;
  rationaleRequiredWhen?: TConditionExpression;
  responsibleFunction?: string;
  /** Binds this question to a template custom field when both represent the same value. */
  bindsToCustomFieldId?: string;
  suggestedImpacts?: string[];
  suggestedActivities?: string[];
  /** A subtle inline note, e.g. "Requires Regulatory Affairs confirmation". */
  confirmationNote?: string;
};

export type TAssessmentSectionConfig = {
  id: string;
  title: string;
  description: string;
  responsibleFunction: string;
  visibleWhen?: TConditionExpression;
  customFieldIds: string[];
  questions: TAssessmentQuestionConfig[];
  /** Marks a platform-level conditional section (safety/reportability, CAPA, containment). */
  platformSection?: 'safetyReportability' | 'capaDetermination' | 'immediateContainment';
};

export const AFFECTED_OBJECT_SUGGESTION = {
  LIKELY_AFFECTED: 'Likely affected',
  REVIEW_REQUIRED: 'Review required',
  PROBABLY_UNAFFECTED: 'Probably unaffected',
  NOT_ASSESSED: 'Not assessed',
} as const;
export type TAffectedObjectSuggestion = (typeof AFFECTED_OBJECT_SUGGESTION)[keyof typeof AFFECTED_OBJECT_SUGGESTION];

export type TCandidateAffectedObjectConfig = {
  id: string;
  label: string;
  documents?: TSparseItem[];
  initialSuggestion: TAffectedObjectSuggestion;
  responsibleFunction?: string;
  /** How the suggestion arose, retained in the instantiated template snapshot. */
  suggestedBy?: string;
  visibleWhen?: TConditionExpression;
};

export type TPreliminaryActivityConfig = {
  id: string;
  title: string;
  description: string;
  responsibleFunction: string;
  owner: string;
  taskType: TTaskType;
  priority: TTaskPriority;
  dueInDays: number;
  completionCriteria: string;
  dependencies?: string[];
  linkedObject?: string;
  documents?: TSparseItem[];
  implementationProposal?: ImplementationProposal;
  visibleWhen?: TConditionExpression;
};

// The organization-configured assessment template. This is the source for newly
// created Changes only; an accepted Change stores an immutable snapshot instead.
export type TAssessmentTemplateConfig = {
  id: string;
  version: number;
  name: string;
  description: string;
  requestPrefills: Partial<Record<TSystemChangeFieldKey, TFieldValue>>;
  customFields: TTemplateCustomFieldConfig[];
  assessmentSections: TAssessmentSectionConfig[];
  candidateAffectedObjects: TCandidateAffectedObjectConfig[];
  preliminaryActivities: TPreliminaryActivityConfig[];
  suggestedAssessorFunctions: string[];
};

// The immutable snapshot instantiated on the Change when its request is submitted.
// Later template edits must never alter this or a signed baseline.
export type TChangeAssessmentTemplateSnapshot = {
  templateId: string;
  templateVersion: number;
  instantiatedAt: string;
  customFields: TTemplateCustomFieldConfig[];
  assessmentSections: TAssessmentSectionConfig[];
  candidateAffectedObjects: TCandidateAffectedObjectConfig[];
  preliminaryActivities: TPreliminaryActivityConfig[];
};
